import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, apiPatch, apiPost } from "@/lib/api";
import { inspectionKeys } from "@/lib/query";

export interface InspectionChecklist {
  id: string;
  projectId: string;
  title: string;
  checklistType: string;
  status: "not_started" | "in_progress" | "completed" | "failed" | "void";
  location: string | null;
  scheduledDate: string | null;
  conductedAt: string | null;
  inspectionPassed: boolean | null;
  createdAt: string;
}

/** Completed, failed, or void inspections are view-only (signed off or closed). */
export function isInspectionChecklistReadOnly(status: InspectionChecklist["status"]): boolean {
  return status === "completed" || status === "failed" || status === "void";
}

export interface ChecklistItem {
  id: string;
  inspectionChecklistId: string;
  itemOrder: number;
  description: string;
  responseType: string;
  isRequired: boolean;
  evidenceRequired: boolean;
  response: string | null;
  responseText: string | null;
  notes: string | null;
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  description: string | null;
  checklistType: string;
  trade: string | null;
}

export function useInspectionChecklists(projectId?: string) {
  return useQuery({
    queryKey: [...inspectionKeys.all, "checklists", projectId],
    queryFn: () =>
      apiFetch<InspectionChecklist[]>(
        projectId ? `/inspection-checklists?projectId=${projectId}` : "/inspection-checklists",
      ),
  });
}

export function useInspectionChecklist(id: string, enabled = true) {
  return useQuery({
    queryKey: [...inspectionKeys.all, "checklists", id],
    queryFn: () => apiFetch<InspectionChecklist>(`/inspection-checklists/${id}`),
    enabled,
  });
}

export function useChecklistItems(inspectionChecklistId: string, enabled = true) {
  return useQuery({
    queryKey: [...inspectionKeys.all, "items", inspectionChecklistId],
    queryFn: () =>
      apiFetch<ChecklistItem[]>(`/inspection-checklists/${inspectionChecklistId}/items`),
    enabled,
  });
}

export function useChecklistTemplates() {
  return useQuery({
    queryKey: [...inspectionKeys.all, "templates"],
    queryFn: () => apiFetch<ChecklistTemplate[]>("/checklist-templates"),
  });
}

export function useCreateInspectionChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      projectId: string;
      checklistTemplateId: string;
      title: string;
      location?: string | null;
      scheduledDate: string;
    }) => apiPost<InspectionChecklist>("/inspection-checklists", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inspectionKeys.all });
      toast.success("Inspection created");
    },
    onError: () => toast.error("Failed to create inspection"),
  });
}

export function useUpdateChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      inspectionChecklistId: _cid,
      ...data
    }: {
      id: string;
      inspectionChecklistId: string;
      response: string;
      responseText?: string;
      notes?: string;
    }) => apiPatch<ChecklistItem>(`/checklist-items/${id}`, data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: [...inspectionKeys.all, "items", vars.inspectionChecklistId],
      });
    },
    onError: () => toast.error("Failed to save response"),
  });
}

export function useSignOffInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiPatch<InspectionChecklist>(`/inspection-checklists/${id}/sign-off`, {}),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: inspectionKeys.all });
      void qc.invalidateQueries({ queryKey: [...inspectionKeys.all, "checklists", id] });
      void qc.invalidateQueries({ queryKey: [...inspectionKeys.all, "items", id] });
      toast.success("Inspection signed off");
    },
    onError: () => toast.error("Failed to sign off inspection"),
  });
}

interface Inspection {
  id: string;
  projectId: string;
  type: string | null;
  date: string | null;
  inspector: string | null;
  status: string | null;
}

export type { Inspection };

export function useInspections(projectId?: string) {
  return useQuery({
    queryKey: [...inspectionKeys.all, "legacy", projectId],
    queryFn: () =>
      apiFetch<Inspection[]>(projectId ? `/inspections?projectId=${projectId}` : "/inspections"),
  });
}
